import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Tab, 
  Tabs, 
  CircularProgress,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import UpdateIcon from '@mui/icons-material/Update';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import CloseIcon from '@mui/icons-material/Close';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import api from '../../../services/api';
import SCurveImport from './SCurveImport';
import SCurveExport from './SCurveExport';
import MSProjectImport from './MSProjectImport';
import { useAuth } from '../../../context/AuthContext';
import { t } from '../../../utils/textUtils';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scurve-tabpanel-${index}`}
      aria-labelledby={`scurve-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ProjectSCurve = ({ projectId, projectData }) => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [sCurveData, setSCurveData] = useState(null);
  const [timeUnit, setTimeUnit] = useState('week');
  const [selectedWeek, setSelectedWeek] = useState('all'); // 'all', 'week1', 'week2', etc.
  const [progressUpdateDate, setProgressUpdateDate] = useState(new Date());
  const [progressUpdateValue, setProgressUpdateValue] = useState(0);
  const [completedQuantityValue, setCompletedQuantityValue] = useState(0);
  const [updateMode, setUpdateMode] = useState('percentage'); // 'percentage' or 'quantity'
  const [selectedTaskId, setSelectedTaskId] = useState('');
  const [autoRefresh] = useState(true);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  
  // New states for per-bangunan S-Curve
  const [viewMode, setViewMode] = useState('project'); // 'project' or 'bangunan'
  const [selectedBangunan, setSelectedBangunan] = useState('all'); // 'all' or specific bangunan ID
  const [bangunanList, setBangunanList] = useState([]);




    
  // Limit the actual-progress line so it does not extend into the future.
  // Uses the real per-period end dates supplied by the backend
  // (weekEndDates / periodEndDates) — no year-guessing from DD/MM labels.
  // If those dates are unavailable, the data is returned unchanged.
  const limitActualDataToToday = (actualData, periodEndDates) => {
    if (!Array.isArray(actualData)) {
      return actualData;
    }
    if (!Array.isArray(periodEndDates) || periodEndDates.length !== actualData.length) {
      return actualData;
    }

    const today = new Date();
    today.setHours(23, 59, 59, 999); // include the whole of today

    const limitedData = [...actualData];
    for (let i = 0; i < limitedData.length; i++) {
      const endDate = new Date(periodEndDates[i]);
      // Null out only periods that genuinely end after today.
      if (!Number.isNaN(endDate.getTime()) && endDate > today) {
        limitedData[i] = null;
      }
    }
    return limitedData;
  };
  
  // Update form values when a task is selected
  useEffect(() => {
    if (selectedTaskId) {
      const selectedTask = tasks.find(task => task.id === selectedTaskId);
      if (selectedTask) {
        // Update progress value
        const progressPercent = selectedTask.progress !== undefined 
          ? Math.round(selectedTask.progress * 100) 
          : 0;
        setProgressUpdateValue(progressPercent);
        
        // Update completed quantity value
        const completedQty = selectedTask.completedQuantity !== undefined 
          ? selectedTask.completedQuantity 
          : (selectedTask.progress * (selectedTask.quantity || 1));
        setCompletedQuantityValue(completedQty);
      }
    }
  }, [selectedTaskId, tasks]);
  
  // Fetch bangunan list for per-bangunan S-Curve
  const fetchBangunanList = useCallback(async () => {
    try {
      const response = await api.get(`/projects/${projectId}/modular-units`);
      if (response.data && response.data.modularUnits) {
        setBangunanList(response.data.modularUnits);
      }
    } catch (err) {
      console.error('Error fetching bangunan list:', err);
      setBangunanList([]);
    }
  }, [projectId]);
  
  // Fetch tasks for progress update functionality
  const fetchTasksForProgressUpdate = useCallback(async () => {
    try {
      // Use Gantt endpoint to get tasks for progress update functionality
      const response = await api.get(`/projects/${projectId}/gantt/flat`);
      
      if (response.data && response.data.success && Array.isArray(response.data.data?.data)) {
        const tasksData = response.data.data.data
          .filter(item => item.type === 'task')
          .map(item => ({
            id: item.id,
            text: item.text,
            name: item.text,
            startDate: item.start_date,
            endDate: item.end_date,
            progress: item.progress || 0,
            quantity: item.quantity || 0,
            completedQuantity: item.completedQuantity || (item.progress * (item.quantity || 1)),
            unit: item.unit || '',
            weight: item.weight || 0,
            parent: item.parent,
            assignedTo: item.assignedTo
          }));
        
        setTasks(tasksData);
      }
    } catch (err) {
      console.error('Error fetching tasks for progress update:', err);
      // Don't set error here as this is secondary functionality
    }
  }, [projectId]);
  
  // Fetch S-Curve data using new endpoint
  const fetchSCurveData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use new S-Curve endpoint that implements the correct logic
      const params = new URLSearchParams({
        timeUnit: timeUnit
      });
      
      // Add weekNumber parameter if specific week is selected
      if (timeUnit === 'week' && selectedWeek !== 'all' && selectedWeek.startsWith('week')) {
        const weekNum = parseInt(selectedWeek.replace('week', ''), 10);
        if (!isNaN(weekNum)) {
          params.append('weekNumber', weekNum);
        }
      }
      
      // Add bangunan parameter if per-bangunan mode is selected
      if (viewMode === 'bangunan' && selectedBangunan !== 'all') {
        params.append('bangunanId', selectedBangunan);
      }
      
      const response = await api.get(`/projects/${projectId}/scurve/data?${params}`);
      
      if (response.data && response.data.success) {
        const data = response.data.data;
        
        // Debug logging untuk memverifikasi data yang diterima
        console.log('S-Curve Data Received:', {
          totalWeight: data.totalWeight,
          timeUnit: data.timeUnit,
          labelsCount: data.labels?.length || 0,
          plannedCumulative: data.plannedCumulative,
          actualCumulative: data.actualCumulative,
          finalPlannedValue: data.plannedCumulative ? data.plannedCumulative[data.plannedCumulative.length - 1] : 0,
          finalActualValue: data.actualCumulative ? data.actualCumulative[data.actualCumulative.length - 1] : 0
        });
        
        // Log detail presisi
        if (data.plannedCumulative && data.plannedCumulative.length > 0) {
          const finalPlanned = data.plannedCumulative[data.plannedCumulative.length - 1];
          console.log('Precision Check:', {
            finalPlannedRaw: finalPlanned,
            finalPlannedFixed6: finalPlanned.toFixed(6),
            differenceFrom100: Math.abs(100 - finalPlanned).toFixed(6),
            isCloseTo100: Math.abs(100 - finalPlanned) < 0.01
          });
        }
        
        // Transform data for Chart.js format
        const chartData = {
          labels: data.labels,
          datasets: [
            {
              label: t('PLAN CUMULATIVE'),
              data: data.plannedCumulative,
              borderColor: 'rgba(54, 162, 235, 1)',
              backgroundColor: 'rgba(54, 162, 235, 0.1)',
              borderWidth: 3,
              fill: false,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6
            },
            {
              label: t('ACTUAL CUMULATIVE'),
              data: limitActualDataToToday(data.actualCumulative, data.weekEndDates),
              borderColor: 'rgba(255, 99, 132, 1)',
              backgroundColor: 'rgba(255, 99, 132, 0.1)',
              borderWidth: 3,
              fill: false,
              tension: 0.4,
              pointRadius: 4,
              pointHoverRadius: 6
            }
          ]
        };
        
        // Add individual progress datasets if available
        if (data.plannedIndividual && data.actualIndividual) {
          chartData.datasets.push(
            {
              label: t('PLAN INDIVIDUAL'),
              data: data.plannedIndividual,
              type: 'bar',
              backgroundColor: 'rgba(54, 162, 235, 0.3)',
              borderColor: 'rgba(54, 162, 235, 0.8)',
              borderWidth: 1,
              yAxisID: 'y1'
            },
            {
              label: t('ACTUAL INDIVIDUAL'),
              data: data.actualIndividual,
              type: 'bar',
              backgroundColor: 'rgba(255, 99, 132, 0.3)',
              borderColor: 'rgba(255, 99, 132, 0.8)',
              borderWidth: 1,
              yAxisID: 'y1'
            }
          );
        }
        
        setSCurveData({
          ...chartData,
          rawData: data,
          totalWeight: data.totalWeight,
          timeUnit: data.timeUnit,
          weekNumber: data.weekNumber
        });
        
        // Also fetch tasks for progress update functionality
        await fetchTasksForProgressUpdate();
        
      } else {
        console.error('Invalid S-Curve response format:', response.data);
        setSCurveData(null);
        setError('Failed to generate S-Curve data. Please check your task data.');
      }
    } catch (err) {
      console.error('Error fetching S-Curve data:', err);
      setError('Failed to load S-Curve data. Please try again later.');
      setSCurveData(null);
    } finally {
      setLoading(false);
    }
  }, [projectId, timeUnit, selectedWeek, viewMode, selectedBangunan, fetchTasksForProgressUpdate]);

  // Listen for Gantt chart updates
  useEffect(() => {
    // Listen for Gantt chart updates
    const handleGanttUpdate = (event) => {
      if (autoRefresh && event.detail && event.detail.projectId === projectId) {
        console.log('S-Curve detected Gantt update, refreshing data...');
        fetchSCurveData();
      }
    };

    window.addEventListener('FORCE_GANTT_REFRESH', handleGanttUpdate);
    
    return () => {
      window.removeEventListener('FORCE_GANTT_REFRESH', handleGanttUpdate);
    };
  }, [projectId, autoRefresh, fetchSCurveData]);

  // Fetch bangunan list when component mounts
  useEffect(() => {
    if (projectId) {
      fetchBangunanList();
    }
  }, [projectId, fetchBangunanList]);

  // Fetch S-Curve data
  useEffect(() => {
    if (projectId) {
      fetchSCurveData();
    }
  }, [projectId, timeUnit, selectedWeek, viewMode, selectedBangunan, fetchSCurveData]);


  // Update progress and refresh S-Curve data
  const updateProgressAndRefresh = async (updatedTasks) => {
    setTasks(updatedTasks);
    // Refresh S-Curve data from backend
    await fetchSCurveData();
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle time unit change
  const handleTimeUnitChange = (event) => {
    setTimeUnit(event.target.value);
    // Reset week selection when changing time unit
    setSelectedWeek('all');
  };
  
  const handleWeekChange = (event) => {
    setSelectedWeek(event.target.value);
  };
  
  // Handle view mode change
  const handleViewModeChange = (event) => {
    setViewMode(event.target.value);
    // Reset bangunan selection when changing view mode
    setSelectedBangunan('all');
  };
  
  // Handle bangunan selection change
  const handleBangunanChange = (event) => {
    setSelectedBangunan(event.target.value);
  };
  
  // Generate week options based on S-Curve data
  const generateWeekOptions = () => {
    if (!sCurveData || !sCurveData.rawData) return [{value: 'all', label: t('SEMUA MINGGU')}];
    
    const data = sCurveData.rawData;
    const options = [{value: 'all', label: t('SEMUA MINGGU')}];
    
    // Generate week options based on the number of labels
    if (data.timeUnit === 'week') {
      for (let i = 0; i < data.labels.length; i++) {
        options.push({value: `week${i + 1}`, label: t(`MINGGU ${i + 1}`)});
      }
    }
    
    return options;
  };
  
  // Handle progress update
  const handleProgressUpdate = async () => {
    if (!selectedTaskId) {
      setError(t('SILAKAN PILIH TUGAS UNTUK DIPERBARUI'));
      return;
    }
    
    try {
      setLoading(true);
      
      // Find the selected task
      const selectedTask = tasks.find(task => task.id === selectedTaskId);
      if (!selectedTask) {
        throw new Error(t('TUGAS YANG DIPILIH TIDAK DITEMUKAN'));
      }
      
      let progress, completedQuantity;
      
      if (updateMode === 'percentage') {
        // Calculate completedQuantity based on progress percentage
        progress = progressUpdateValue / 100; // Convert to decimal (0-1)
        completedQuantity = progress * (selectedTask.quantity || 1);
      } else {
        // Calculate progress based on completedQuantity
        completedQuantity = completedQuantityValue;
        progress = selectedTask.quantity > 0 
          ? Math.min(1, Math.max(0, completedQuantity / selectedTask.quantity)) 
          : 0;
      }
      
      // Send both progress and completedQuantity to ensure consistency
      const response = await api.put(`/projects/${projectId}/tasks/${selectedTaskId}`, {
        progress: progress,
        completedQuantity: completedQuantity
      });
      
      if (response.data) {
        // Update tasks list
        const updatedTasks = tasks.map(task => 
          task.id === selectedTaskId ? { 
            ...task, 
            progress: progress,
            completedQuantity: completedQuantity
          } : task
        );
        
        await updateProgressAndRefresh(updatedTasks);
        
        // Reset form
        setSelectedTaskId('');
        setProgressUpdateValue(0);
        setCompletedQuantityValue(0);
      }
    } catch (err) {
      console.error('Error updating task progress:', err);
      setError(t('GAGAL MEMPERBARUI PROGRES TUGAS. SILAKAN COBA LAGI.'));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle import from Excel
  const handleImportFromExcel = (importedData) => {
    try {
      setLoading(true);
      
      // Process imported data
      // This is a simplified version - in a real app, you'd want to validate and process this data more carefully
      const chartData = {
        labels: importedData.map(item => item.date),
        datasets: [
          {
            label: 'Planned Progress',
            data: importedData.map(item => item.planned),
            borderColor: 'rgba(54, 162, 235, 1)',
            backgroundColor: 'rgba(54, 162, 235, 0.2)',
            borderWidth: 2,
            fill: true,
            tension: 0.4
          },
          {
            label: 'Actual Progress',
            data: importedData.map(item => item.actual),
            borderColor: 'rgba(255, 159, 64, 1)',
            backgroundColor: 'rgba(255, 159, 64, 0.2)',
            borderWidth: 2,
            fill: true,
            tension: 0.4
          }
        ]
      };
      
      // Set S-Curve data
      setSCurveData({
        chartData,
        importedData: true
      });
      
      // Switch to chart tab
      setTabValue(0);
    } catch (err) {
      console.error('Error processing imported data:', err);
      setError(t('GAGAL MEMPROSES DATA IMPOR. SILAKAN PERIKSA FILE EXCEL ANDA.'));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle import from MS Project
  const handleImportFromMSProject = (importedData) => {
    try {
      setLoading(true);
      
      // Process imported data from MS Project
      // This would require parsing the XML data from MS Project
      // For now, we'll use a simplified version similar to Excel import
      
      // Set S-Curve data
      setSCurveData({
        chartData: importedData.chartData,
        importedData: true
      });
      
      // Switch to chart tab
      setTabValue(0);
    } catch (err) {
      console.error('Error processing MS Project data:', err);
      setError(t('GAGAL MEMPROSES DATA MS PROJECT. SILAKAN PERIKSA FILE ANDA.'));
    } finally {
      setLoading(false);
    }
  };
  
  // Check if user has edit permission
  const hasEditPermission = () => {
    if (!user) return false;
    
    // Admin can edit any project
    if (user.role === 'admin' || user.role === 'Director') return true;
    
    // Project manager cannot edit projects (only view)
    if (user.role === 'project_manager') {
      return false;
    }
    
    return false;
  };
  
  // Check if user can update progress via S-Curve
  // Only project managers and directors can update progress via S-Curve
  const canUpdateProgressViaSCurve = () => {
    if (!user) return false;
    
    // Only admin and Director roles can update progress via S-Curve (project_manager cannot)
    if (user.role === 'admin' || user.role === 'Director') {
      return true;
    }
    
    return false;
  };
  
  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: t('PROGRES KUMULATIF (%)')
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        title: {
          display: true,
          text: t('PROGRES INDIVIDUAL (%)')
        },
        grid: {
          drawOnChartArea: false,
        },
      },
      x: {
        title: {
          display: true,
          text: sCurveData?.timeUnit === 'day' ? t('HARI') : 
                (timeUnit === 'week' ? t('MINGGU') : t('BULAN'))
        }
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function(context) {
            const suffix = context.dataset.label.includes('Individual') ? '%' : '%';
            return `${context.dataset.label}: ${context.parsed.y.toFixed(2)}${suffix}`;
          }
        }
      },
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: sCurveData?.weekNumber ? 
              t(`KURVA-S PROYEK - MINGGU ${sCurveData.weekNumber} TAMPILAN HARIAN`) :
              t('KURVA-S PROYEK (PROGRES RENCANA VS AKTUAL)')
      }
    }
  };
  
  // Chart options for fullscreen
  const fullscreenChartOptions = {
    ...chartOptions,
    maintainAspectRatio: false,
    plugins: {
      ...chartOptions.plugins,
      title: {
        display: true,
        text: t('KURVA-S PROYEK (PROGRES RENCANA VS AKTUAL) - TAMPILAN LAYAR PENUH'),
        font: {
          size: 18
        }
      }
    }
  };
  
  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#5D4037' }}>
          {t('KURVA-S PROYEK')}
        </Typography>
        
        <Box>
          <FormControl sx={{ minWidth: 120, mr: 2 }}>
            <InputLabel id="time-unit-label">{t('SATUAN WAKTU')}</InputLabel>
            <Select
              labelId="time-unit-label"
              id="time-unit-select"
              value={timeUnit}
              label={t('SATUAN WAKTU')}
              onChange={handleTimeUnitChange}
              size="small"
            >
              <MenuItem value="week">{t('MINGGUAN')}</MenuItem>
              <MenuItem value="month">{t('BULANAN')}</MenuItem>
            </Select>
          </FormControl>
          
          {/* Week filter dropdown - only show when Weekly time unit is selected */}
          {timeUnit === 'week' && (
            <FormControl sx={{ minWidth: 120, mr: 2 }}>
              <InputLabel id="week-filter-label">{t('MINGGU')}</InputLabel>
              <Select
                labelId="week-filter-label"
                id="week-filter-select"
                value={selectedWeek}
                label={t('MINGGU')}
                onChange={handleWeekChange}
                size="small"
              >
                {generateWeekOptions().map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          {/* View Mode Selection */}
          <FormControl sx={{ minWidth: 150, mr: 2 }}>
            <InputLabel id="view-mode-label">{t('TAMPILAN')}</InputLabel>
            <Select
              labelId="view-mode-label"
              id="view-mode-select"
              value={viewMode}
              label={t('TAMPILAN')}
              onChange={handleViewModeChange}
              size="small"
            >
              <MenuItem value="project">{t('KURVA S PROYEK')}</MenuItem>
              <MenuItem value="bangunan">{t('KURVA S PER BANGUNAN')}</MenuItem>
            </Select>
          </FormControl>
          
          {/* Bangunan Selection - only show when per-bangunan mode is selected */}
          {viewMode === 'bangunan' && (
            <FormControl sx={{ minWidth: 200, mr: 2 }}>
              <InputLabel id="bangunan-filter-label">{t('BANGUNAN')}</InputLabel>
              <Select
                labelId="bangunan-filter-label"
                id="bangunan-filter-select"
                value={selectedBangunan}
                label={t('BANGUNAN')}
                onChange={handleBangunanChange}
                size="small"
              >
                <MenuItem value="all">{t('SEMUA BANGUNAN')}</MenuItem>
                {bangunanList.map((bangunan) => (
                  <MenuItem key={bangunan.id} value={bangunan.id}>
                    {bangunan.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          
          {/* Export button - available to all users */}
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => setTabValue(3)}
            sx={{ 
              mr: 1,
              color: '#5D4037', 
              borderColor: '#5D4037',
              '&:hover': { 
                backgroundColor: '#F9F5EB',
                borderColor: '#8D6E63'
              }
            }}
          >
            {t('EKSPOR')}
          </Button>
          
          {/* Import button - only for users with edit permission */}
          {hasEditPermission() && (
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => setTabValue(2)}
              sx={{ 
                bgcolor: '#F0C14B', 
                '&:hover': { bgcolor: '#DDA726' },
                color: '#5D4037',
                fontWeight: 'bold'
              }}
            >
              {t('IMPOR')}
            </Button>
          )}
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}
      
      <Paper sx={{ 
        backgroundColor: '#FFFFFF', 
        borderRadius: 1,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        mb: 3,
        overflow: 'hidden'
      }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': { 
              fontSize: '0.9rem',
              color: '#5D4037',
              fontWeight: 500,
              textTransform: 'none'
            },
            '& .Mui-selected': { 
              color: '#F0C14B',
              fontWeight: 600
            },
            '& .MuiTabs-indicator': { 
              backgroundColor: '#F0C14B'
            }
          }}
        >
          <Tab label={t('GRAFIK KURVA-S')} />
          {canUpdateProgressViaSCurve() && <Tab label={t('PERBARUI PROGRES')} />}
          {hasEditPermission() && <Tab label={t('IMPOR DARI EXCEL')} />}
          <Tab label={t('EKSPOR DATA')} />
          {hasEditPermission() && <Tab label={t('IMPOR DARI MS PROJECT')} />}
        </Tabs>
        
        {/* S-Curve Chart Tab */}
        <TabPanel value={tabValue} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: '#F0C14B' }} />
            </Box>
          ) : sCurveData ? (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#5D4037' }}>
                  {viewMode === 'bangunan' 
                    ? `${t('VISUALISASI KURVA-S')} - ${selectedBangunan === 'all' ? t('SEMUA BANGUNAN') : bangunanList.find(b => b.id === selectedBangunan)?.name || t('BANGUNAN')}`
                    : t('VISUALISASI KURVA-S')
                  }
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<FullscreenIcon />}
                  onClick={() => setFullscreenOpen(true)}
                  sx={{ 
                    color: '#5D4037', 
                    borderColor: '#5D4037',
                    '&:hover': { 
                      backgroundColor: '#F9F5EB',
                      borderColor: '#8D6E63'
                    }
                  }}
                >
                  {t('LAYAR PENUH')}
                </Button>
              </Box>
              <Box sx={{ p: 2 }}>
                {/* Tinggi chart dikurangi untuk memberikan ruang untuk nilai persentase di bawahnya */}
                <Box sx={{ height: 350 }}>
                  <Line data={sCurveData} options={chartOptions} />
                </Box>
                
                {/* Nilai persentase mingguan langsung di bawah chart */}
                <Box sx={{ mt: 2, overflowX: 'auto' }}>
                  <Table size="small" sx={{ borderCollapse: 'separate', borderSpacing: '0' }}>
                    <TableHead>
                      <TableRow>
                        {/* Header kosong untuk kolom pertama */}
                        <TableCell sx={{ border: 'none', width: '80px', p: 0.5 }}></TableCell>
                        {sCurveData.rawData.labels.map((label, index) => (
                          <TableCell 
                            key={`week-${index}`} 
                            align="center" 
                            sx={{ 
                              border: 'none', 
                              p: 0.5, 
                              minWidth: '80px',
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              color: '#5D4037'
                            }}
                          >
                            {label.replace('Week ', 'W')}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {/* Plan Individual */}
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            border: 'none', 
                            p: 0.5, 
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            color: 'rgba(54, 162, 235, 1)'
                          }}
                        >
                          {t('PLAN IND.')}
                        </TableCell>
                        {sCurveData.rawData.plannedIndividual.map((value, index) => (
                          <TableCell 
                            key={`plan-ind-${index}`} 
                            align="center"
                            sx={{ 
                              border: 'none', 
                              p: 0.5,
                              fontSize: '0.75rem',
                              backgroundColor: 'rgba(54, 162, 235, 0.1)'
                            }}
                          >
                            {value.toFixed(2)}%
                          </TableCell>
                        ))}
                      </TableRow>
                      
                      {/* Actual Individual */}
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            border: 'none', 
                            p: 0.5, 
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            color: 'rgba(255, 99, 132, 1)'
                          }}
                        >
                          {t('ACTUAL IND.')}
                        </TableCell>
                        {sCurveData.rawData.actualIndividual.map((value, index) => (
                          <TableCell 
                            key={`actual-ind-${index}`} 
                            align="center"
                            sx={{ 
                              border: 'none', 
                              p: 0.5,
                              fontSize: '0.75rem',
                              backgroundColor: 'rgba(255, 99, 132, 0.1)'
                            }}
                          >
                            {value.toFixed(2)}%
                          </TableCell>
                        ))}
                      </TableRow>
                      
                      {/* Deviasi Individual */}
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            border: 'none', 
                            p: 0.5, 
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {t('DEV. IND.')}
                        </TableCell>
                        {sCurveData.rawData.actualIndividual.map((value, index) => {
                          const plannedValue = sCurveData.rawData.plannedIndividual[index] || 0;
                          const deviation = value - plannedValue;
                          const color = deviation > 0 ? '#4CAF50' : deviation < 0 ? '#F44336' : 'inherit';
                          
                          return (
                            <TableCell 
                              key={`dev-ind-${index}`} 
                              align="center"
                              sx={{ 
                                border: 'none', 
                                p: 0.5,
                                fontSize: '0.75rem',
                                color: color
                              }}
                            >
                              {deviation.toFixed(2)}%
                            </TableCell>
                          );
                        })}
                      </TableRow>
                      
                      {/* Plan Cumulative */}
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            border: 'none', 
                            p: 0.5, 
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            color: 'rgba(54, 162, 235, 1)'
                          }}
                        >
                          {t('PLAN CUM.')}
                        </TableCell>
                        {sCurveData.rawData.plannedCumulative.map((value, index) => (
                          <TableCell 
                            key={`plan-cum-${index}`} 
                            align="center"
                            sx={{ 
                              border: 'none', 
                              p: 0.5,
                              fontSize: '0.75rem',
                              backgroundColor: 'rgba(54, 162, 235, 0.1)'
                            }}
                          >
                            {value.toFixed(2)}%
                          </TableCell>
                        ))}
                      </TableRow>
                      
                      {/* Actual Cumulative */}
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            border: 'none', 
                            p: 0.5, 
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            color: 'rgba(255, 99, 132, 1)'
                          }}
                        >
                          {t('ACTUAL CUM.')}
                        </TableCell>
                        {sCurveData.rawData.actualCumulative.map((value, index) => (
                          <TableCell 
                            key={`actual-cum-${index}`} 
                            align="center"
                            sx={{ 
                              border: 'none', 
                              p: 0.5,
                              fontSize: '0.75rem',
                              backgroundColor: 'rgba(255, 99, 132, 0.1)'
                            }}
                          >
                            {value ? value.toFixed(2) + '%' : '-'}
                          </TableCell>
                        ))}
                      </TableRow>
                      
                      {/* Deviasi Cumulative */}
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            border: 'none', 
                            p: 0.5, 
                            fontSize: '0.75rem',
                            fontWeight: 'bold'
                          }}
                        >
                          {t('DEV. CUM.')}
                        </TableCell>
                        {sCurveData.rawData.actualCumulative.map((value, index) => {
                          const plannedValue = sCurveData.rawData.plannedCumulative[index] || 0;
                          const deviation = value ? value - plannedValue : null;
                          const color = deviation > 0 ? '#4CAF50' : deviation < 0 ? '#F44336' : 'inherit';
                          
                          return (
                            <TableCell 
                              key={`dev-cum-${index}`} 
                              align="center"
                              sx={{ 
                                border: 'none', 
                                p: 0.5,
                                fontSize: '0.75rem',
                                color: color
                              }}
                            >
                              {deviation !== null ? deviation.toFixed(2) + '%' : '-'}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {t('TIDAK ADA DATA KURVA-S TERSEDIA. SILAKAN BUAT TUGAS DI GANTT CHART TERLEBIH DAHULU ATAU IMPOR DATA.')}
              </Typography>
              <Button
                variant="outlined"
                startIcon={<UploadFileIcon />}
                onClick={() => setTabValue(2)}
                sx={{ 
                  color: '#5D4037', 
                  borderColor: '#5D4037',
                  '&:hover': { 
                    backgroundColor: '#F9F5EB',
                    borderColor: '#8D6E63'
                  }
                }}
              >
                {t('IMPOR DATA')}
              </Button>
            </Box>
          )}
        </TabPanel>
        
        {/* Update Progress Tab */}
        <TabPanel value={tabValue} index={1}>
          {canUpdateProgressViaSCurve() ? (
            <>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: '#5D4037' }}>
                {t('PERBARUI PROGRES TUGAS')}
              </Typography>
              
              {tasks.length > 0 ? (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth sx={{ mb: 3 }}>
                      <InputLabel id="task-select-label">{t('PILIH TUGAS')}</InputLabel>
                      <Select
                        labelId="task-select-label"
                        id="task-select"
                        value={selectedTaskId}
                        label={t('PILIH TUGAS')}
                        onChange={(e) => setSelectedTaskId(e.target.value)}
                      >
                        {tasks.map(task => {
                          // Calculate progress percentage
                          const progressPercent = task.completedQuantity !== undefined && task.quantity > 0
                            ? Math.min(100, Math.max(0, (task.completedQuantity / task.quantity) * 100))
                            : (task.progress || 0) * 100;
                          
                          // Format display text
                          const quantityText = task.completedQuantity !== undefined && task.quantity > 0
                            ? `${task.completedQuantity.toFixed(2)}/${task.quantity} ${task.unit || ''}`
                            : '';
                          
                          return (
                            <MenuItem key={task.id} value={task.id}>
                              {task.name} ({progressPercent.toFixed(1)}% complete{quantityText ? ` - ${quantityText}` : ''})
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </FormControl>
                    
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel id="update-mode-label">{t('MODE PEMBARUAN')}</InputLabel>
                      <Select
                        labelId="update-mode-label"
                        id="update-mode-select"
                        value={updateMode}
                        label={t('MODE PEMBARUAN')}
                        onChange={(e) => setUpdateMode(e.target.value)}
                      >
                        <MenuItem value="percentage">{t('PERBARUI BERDASARKAN PERSENTASE')}</MenuItem>
                        <MenuItem value="quantity">{t('PERBARUI BERDASARKAN KUANTITAS')}</MenuItem>
                      </Select>
                    </FormControl>
                    
                    {updateMode === 'percentage' ? (
                      <TextField
                        label={t('PROGRES (%)')}
                        type="number"
                        value={progressUpdateValue}
                        onChange={(e) => setProgressUpdateValue(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                        InputProps={{ inputProps: { min: 0, max: 100 } }}
                        fullWidth
                        sx={{ mb: 3 }}
                      />
                    ) : (
                      <Box sx={{ mb: 3 }}>
                        {selectedTaskId && (() => {
                          const selectedTask = tasks.find(task => task.id === selectedTaskId);
                          return (
                            <TextField
                              label={t(`KUANTITAS SELESAI (${selectedTask?.unit || 'UNIT'})`)}
                              type="number"
                              value={completedQuantityValue}
                              onChange={(e) => {
                                const value = parseFloat(e.target.value) || 0;
                                const maxValue = selectedTask?.quantity || 0;
                                setCompletedQuantityValue(Math.max(0, Math.min(maxValue, value)));
                              }}
                              InputProps={{ 
                                inputProps: { 
                                  min: 0, 
                                  max: selectedTask?.quantity || 0,
                                  step: 0.01
                                },
                                endAdornment: <Typography variant="caption" sx={{ ml: 1 }}>{selectedTask?.unit || ''}</Typography>
                              }}
                              helperText={t(`MAKSIMUM: ${selectedTask?.quantity || 0} ${selectedTask?.unit || ''}`)}
                              fullWidth
                            />
                          );
                        })()}
                      </Box>
                    )}
                    
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label={t('TANGGAL PEMBARUAN')}
                        value={progressUpdateDate}
                        onChange={(date) => setProgressUpdateDate(date)}
                        renderInput={(params) => (
                          <TextField {...params} fullWidth sx={{ mb: 3 }} />
                        )}
                      />
                    </LocalizationProvider>
                    
                    <Button
                      variant="contained"
                      startIcon={<UpdateIcon />}
                      onClick={handleProgressUpdate}
                      disabled={!selectedTaskId || loading}
                      sx={{ 
                        bgcolor: '#F0C14B', 
                        '&:hover': { bgcolor: '#DDA726' },
                        color: '#5D4037',
                        fontWeight: 'bold'
                      }}
                    >
                      {t('PERBARUI PROGRES')}
                    </Button>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                      {t('PROGRES PROYEK SAAT INI')}
                    </Typography>
                    
                    {sCurveData && (
                      <Box>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>{t('PROGRES RENCANA')}:</strong> {sCurveData.plannedProgress[sCurveData.plannedProgress.length - 1]}%
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>{t('PROGRES AKTUAL')}:</strong> {sCurveData.actualProgress[sCurveData.actualProgress.length - 1]}%
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          <strong>{t('SELISIH')}:</strong> {(sCurveData.actualProgress[sCurveData.actualProgress.length - 1] - sCurveData.plannedProgress[sCurveData.plannedProgress.length - 1]).toFixed(2)}%
                        </Typography>
                        
                        <Alert 
                          severity={
                            sCurveData.actualProgress[sCurveData.actualProgress.length - 1] >= sCurveData.plannedProgress[sCurveData.plannedProgress.length - 1] 
                              ? 'success' 
                              : 'warning'
                          }
                          sx={{ mt: 2 }}
                        >
                          {sCurveData.actualProgress[sCurveData.actualProgress.length - 1] >= sCurveData.plannedProgress[sCurveData.plannedProgress.length - 1]
                            ? t('PROYEK BERJALAN SESUAI ATAU LEBIH CEPAT DARI JADWAL.')
                            : t('PROYEK TERTINGGAL DARI JADWAL. PERTIMBANGKAN UNTUK MENINJAU TIMELINE ATAU MENGALOKASIKAN LEBIH BANYAK SUMBER DAYA.')}
                        </Alert>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              ) : (
                <Alert severity="info">
                  No tasks available. Please create tasks in the Gantt Chart first.
                </Alert>
              )}
            </>
          ) : (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Access Denied
              </Typography>
              <Typography variant="body2">
                Only Project Managers and Directors are authorized to update progress via S-Curve. Please use the Progress Submission form for regular updates.
              </Typography>
            </Alert>
          )}
        </TabPanel>
        
        {/* Import from Excel Tab - Only shown if user has edit permission */}
        {hasEditPermission() && (
          <TabPanel value={tabValue} index={2}>
            <SCurveImport onImport={handleImportFromExcel} />
          </TabPanel>
        )}
        
        {/* Export Data Tab - Available to all users */}
        <TabPanel value={tabValue} index={3}>
          <SCurveExport sCurveData={sCurveData} projectName={projectData?.name || 'Project'} />
        </TabPanel>
        
        {/* Import from MS Project Tab - Only shown if user has edit permission */}
        {hasEditPermission() && (
          <TabPanel value={tabValue} index={4}>
            <MSProjectImport onImport={handleImportFromMSProject} />
          </TabPanel>
        )}
      </Paper>
      
      {/* Fullscreen Dialog */}
      <Dialog
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            width: '95vw',
            height: '90vh',
            maxWidth: 'none',
            maxHeight: 'none'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: '#F9F5EB',
          color: '#5D4037'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            S-Curve Chart - Fullscreen View
          </Typography>
          <IconButton
            onClick={() => setFullscreenOpen(false)}
            sx={{ color: '#5D4037' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, height: 'calc(100% - 64px)' }}>
          {sCurveData && (
            <Box sx={{ height: '100%', width: '100%' }}>
              <Line data={sCurveData} options={fullscreenChartOptions} />
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ProjectSCurve;
